import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
    ApplyCompanyHistoricalLedgerImportRequest,
    apiClient,
    AdminPulseSubscriptionInput,
    AdminListParams,
    AdminProfessionalServiceMutationRequest,
    AdminCreditPricingConfigParams,
    AdminCreditSowParams,
    ChangeSowPulseSeatQuantityRequest,
    CreateCreditEventRequest,
    CreateCreditImportRequest,
    CreateCreditReservationRequest,
    CreditReservationFilters,
    CreditReservationAdjustmentRequest,
    CreditReservationMutationRequest,
    CreatePricingConfigVersionRequest,
    CreatePricingConfigRequest,
    CreateSowHistoricalImportPreviewRequest,
    CreateSowRequest,
    ReassignLedgerEntryRequest,
    SowFilters,
    CreateAdminCompanyRequest,
    CreateAdminUserRequest,
    AdminCompanySyncResponse,
    AdminCompanySyncSSEEvent,
    AdminUserSyncResponse,
    AdminUserSyncSSEEvent,
    AdminAuth0BulkSyncEvent,
    AdminAuth0BulkSyncStartRequest,
    UpdatePricingConfigRequest,
    UpdateAdminCompanyRequest,
    UpdateAdminCompanyUserAdminRequest,
    UpdateAdminUserRequest,
    UpdateSowRequest,
    UpdateSowWriteAccessRequest,
} from '../services/api';
import { apiConfig } from '../config/auth0';
import { useCallback } from 'react';
import {
    apiQueryRoot,
    getInvalidateAdminCompanyQueryKeys,
    getInvalidateAdminCompanySyncQueryKeys,
    getInvalidateAdminUserQueryKeys,
    normalizeCompanySyncRequest,
} from './useApi.helpers';

// Query keys factory for better cache management
export const queryKeys = {
    all: apiQueryRoot,
    teams: () => [...queryKeys.all, 'teams'] as const,
    users: () => [...queryKeys.all, 'users'] as const,
    auth0Users: () => [...queryKeys.all, 'auth0-users'] as const,
    userProfile: () => [...queryKeys.all, 'user-profile'] as const,
    organizationUsers: (orgId: string) => [...queryKeys.all, 'organization-users', orgId] as const,
    organizationTeams: (orgId: string) => [...queryKeys.all, 'organization-teams', orgId] as const,
    organizationGroups: (orgId: string) =>
        [...queryKeys.all, 'organization-groups', orgId] as const,
    userDetail: (userId: string, orgId?: string) =>
        [...queryKeys.all, 'user-detail', userId, orgId || 'default'] as const,
    sows: (filters: SowFilters = {}) => [...queryKeys.all, 'sows', filters] as const,
    sow: (sowId: string) => [...queryKeys.all, 'sow', sowId] as const,
    sowLedger: (sowId: string) => [...queryKeys.all, 'sow-ledger', sowId] as const,
    sowAudit: (sowId: string) => [...queryKeys.all, 'sow-audit', sowId] as const,
    sowWriteAccess: (sowId: string) => [...queryKeys.all, 'sow-write-access', sowId] as const,
    sowPricingConfig: (sowId: string) => [...queryKeys.all, 'sow-pricing-config', sowId] as const,
    sowBalances: (filters: SowFilters = {}) => [...queryKeys.all, 'sow-balances', filters] as const,
    pricingConfigs: (includeInactive: boolean) =>
        [...queryKeys.all, 'pricing-configs', includeInactive] as const,
    adminCompanies: (params: AdminListParams) =>
        [...queryKeys.all, 'admin-companies', params] as const,
    adminCompany: (companyId: number) => [...queryKeys.all, 'admin-company', companyId] as const,
    adminCompanyAdmins: (companyId: number) =>
        [...queryKeys.all, 'admin-company-admins', companyId] as const,
    adminUsers: (params: AdminListParams) => [...queryKeys.all, 'admin-users', params] as const,
    adminProfessionalServices: (params: AdminListParams) =>
        [...queryKeys.all, 'admin-professional-services', params] as const,
    adminIdentityAttention: (params: AdminListParams) =>
        [...queryKeys.all, 'admin-identity-attention', params] as const,
    adminAuth0BulkSyncCandidates: (unsyncedOnly: boolean) =>
        [...queryKeys.all, 'admin-auth0-bulk-sync-candidates', unsyncedOnly] as const,
    adminAuth0BulkSyncJob: (jobId: string) =>
        [...queryKeys.all, 'admin-auth0-bulk-sync-job', jobId] as const,
    adminUserEditOptions: (query: string | undefined, pageSize: number) =>
        [...queryKeys.all, 'admin-user-edit-options', query ?? '', pageSize] as const,
    adminCreditSows: (params: AdminCreditSowParams) =>
        [...queryKeys.all, 'admin-credit-sows', params] as const,
    adminCreditPricingConfigs: (params: AdminCreditPricingConfigParams) =>
        [...queryKeys.all, 'admin-credit-pricing-configs', params] as const,
    adminPricingConfigActiveSows: (target: {
        pricingConfigIds?: readonly string[];
        pricingConfigGroupId?: string;
    }) => [...queryKeys.all, 'admin-pricing-config-active-sows', target] as const,
    adminCreditPulseSubscriptionTiers: (forSowCreation?: boolean) =>
        [
            ...queryKeys.all,
            'admin-credit-pulse-subscription-tiers',
            { forSowCreation: forSowCreation === true },
        ] as const,
    adminTeamMembers: (sortedTeamIds: string) =>
        [...queryKeys.all, 'admin-team-members', sortedTeamIds] as const,
    adminCompanyTeams: (companyId: number) =>
        [...queryKeys.all, 'admin-company-teams', companyId] as const,
    adminCompanyPulseSubscriptions: (companyId: number) =>
        [...queryKeys.all, 'admin-company-pulse-subscriptions', companyId] as const,
    companySows: () => [...queryKeys.all, 'company-sows'] as const,
    companySowBalances: () => [...queryKeys.all, 'company-sow-balances'] as const,
    companySowUsage: (sowId: string) => [...queryKeys.all, 'company-sow-usage', sowId] as const,
    creditReservations: (filters: CreditReservationFilters = {}) =>
        [...queryKeys.all, 'credit-reservations', filters] as const,
    creditReservation: (reservationId: string) =>
        [...queryKeys.all, 'credit-reservation', reservationId] as const,
};

// Generic API hook for custom API calls
function useApi() {
    const { getAccessTokenSilently } = useAuth0();

    const apiCall = useCallback(
        async <T>(path: string, options?: RequestInit): Promise<T> => {
            const token = await getAccessTokenSilently();
            const baseUrl = apiConfig.accessApiUrl;

            const response = await fetch(`${baseUrl}${path}`, {
                ...options,
                headers: {
                    ...options?.headers,
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || `API call failed: ${response.statusText}`);
            }

            // Handle 204 No Content responses
            if (response.status === 204) {
                return undefined as T;
            }

            // Check if response has content before parsing JSON
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return response.json();
            }

            // For non-JSON responses, return text
            return response.text() as Promise<T>;
        },
        [getAccessTokenSilently],
    );

    return { apiCall };
}

export function useSows(filters: SowFilters = {}, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.sows(filters),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getSows(token, filters);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useSow(sowId: string | null) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.sow(sowId || ''),
        queryFn: async () => {
            if (!sowId) throw new Error('SOW ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getSow(token, sowId);
        },
        enabled: !!sowId,
        staleTime: 2 * 60 * 1000,
    });
}

export function useSowLedger(sowId: string | null) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.sowLedger(sowId || ''),
        queryFn: async () => {
            if (!sowId) throw new Error('SOW ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getSowLedger(token, sowId);
        },
        enabled: !!sowId,
        staleTime: 2 * 60 * 1000,
    });
}

export function useSowAudit(sowId: string | null, options?: { enabled?: boolean }) {
    const { getAccessTokenSilently } = useAuth0();
    const enabled = options?.enabled !== false && !!sowId;

    return useQuery({
        queryKey: queryKeys.sowAudit(sowId || ''),
        queryFn: async () => {
            if (!sowId) throw new Error('SOW ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getSowAudit(token, sowId);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useSowWriteAccess(sowId: string | null, options?: { enabled?: boolean }) {
    const { getAccessTokenSilently } = useAuth0();
    const enabled = options?.enabled !== false && !!sowId;

    return useQuery({
        queryKey: queryKeys.sowWriteAccess(sowId || ''),
        queryFn: async () => {
            if (!sowId) throw new Error('SOW ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getSowWriteAccess(token, sowId);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useSowPricingConfig(sowId: string | null, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.sowPricingConfig(sowId || ''),
        queryFn: async () => {
            if (!sowId) throw new Error('SOW ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getSowPricingConfig(token, sowId);
        },
        enabled: enabled && !!sowId,
        staleTime: 10 * 60 * 1000,
    });
}

export function useCreditReservations(filters: CreditReservationFilters = {}, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.creditReservations(filters),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.listCreditReservations(token, filters);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useCreditReservation(reservationId: string | null) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.creditReservation(reservationId || ''),
        queryFn: async () => {
            if (!reservationId) throw new Error('Reservation ID is required');
            const token = await getAccessTokenSilently();
            return apiClient.getCreditReservation(token, reservationId);
        },
        enabled: !!reservationId,
        staleTime: 2 * 60 * 1000,
    });
}

export function usePricingConfigs(includeInactive = false, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.pricingConfigs(includeInactive),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getPricingConfigs(token, includeInactive);
        },
        enabled,
        staleTime: 10 * 60 * 1000,
    });
}

export function useAdminCompanies(params: AdminListParams, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminCompanies(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCompanies(token, params);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCompany(companyId: number | null) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminCompany(companyId ?? 0),
        queryFn: async () => {
            if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                throw new Error('Company ID is required');
            }
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCompany(token, companyId);
        },
        enabled: typeof companyId === 'number' && Number.isFinite(companyId) && companyId > 0,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCompanyAdmins(companyId: number | null) {
    const { getAccessTokenSilently } = useAuth0();
    const enabled = typeof companyId === 'number' && Number.isFinite(companyId) && companyId > 0;

    return useQuery({
        queryKey: queryKeys.adminCompanyAdmins(companyId ?? 0),
        queryFn: async () => {
            if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                throw new Error('Company ID is required');
            }
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCompanyAdmins(token, companyId);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminUsers(params: AdminListParams, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminUsers(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminUsers(token, params);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminProfessionalServices(params: AdminListParams, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminProfessionalServices(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminProfessionalServices(token, params);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminProfessionalServiceMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const invalidateProfessionalServices = async () => {
        await queryClient.invalidateQueries({
            queryKey: [...queryKeys.all, 'admin-professional-services'],
        });
    };

    return {
        createProfessionalService: useMutation({
            mutationFn: async (request: AdminProfessionalServiceMutationRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createAdminProfessionalService(token, request);
            },
            onSuccess: invalidateProfessionalServices,
        }),
        updateProfessionalService: useMutation({
            mutationFn: async ({
                professionalServiceId,
                request,
            }: {
                professionalServiceId: string;
                request: AdminProfessionalServiceMutationRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updateAdminProfessionalService(
                    token,
                    professionalServiceId,
                    request,
                );
            },
            onSuccess: invalidateProfessionalServices,
        }),
        deactivateProfessionalService: useMutation({
            mutationFn: async (professionalServiceId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.deactivateAdminProfessionalService(token, professionalServiceId);
            },
            onSuccess: invalidateProfessionalServices,
        }),
    };
}

export function useAdminUserEditOptions(query: string | undefined, pageSize = 25, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();
    const normalizedQuery = query?.trim() || undefined;

    return useInfiniteQuery({
        queryKey: queryKeys.adminUserEditOptions(normalizedQuery, pageSize),
        queryFn: async ({ pageParam }) => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminUserEditOptions(token, {
                query: normalizedQuery,
                page: pageParam,
                pageSize,
            });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminUserMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const invalidateUserData = async () => {
        await Promise.all(
            getInvalidateAdminUserQueryKeys().map((queryKey) =>
                queryClient.invalidateQueries({ queryKey }),
            ),
        );
    };

    return {
        invalidateUserData,
        createUser: useMutation({
            mutationFn: async (request: CreateAdminUserRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createAdminUser(token, request);
            },
            onSuccess: invalidateUserData,
        }),
        updateUser: useMutation({
            mutationFn: async ({
                userId,
                request,
            }: {
                userId: number;
                request: UpdateAdminUserRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updateAdminUser(token, userId, request);
            },
            onSuccess: invalidateUserData,
        }),
        syncUserToAuth0: useMutation({
            mutationFn: async (userId: number) => {
                const token = await getAccessTokenSilently();
                return apiClient.syncAdminUserToAuth0(token, userId);
            },
            onSuccess: invalidateUserData,
        }),
        syncUserToAuth0SSE: async (
            userId: number,
            onMessage: (data: AdminUserSyncSSEEvent) => void,
            onError?: (error: Error) => void,
            onComplete?: (result?: AdminUserSyncResponse) => void,
        ) => {
            const token = await getAccessTokenSilently();
            return apiClient.syncAdminUserToAuth0SSE(token, userId, onMessage, onError, onComplete);
        },
        syncUsersToAuth0: useMutation({
            mutationFn: async (request: {
                companyId?: number;
                organizationId?: string;
                unsyncedOnly?: boolean;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.syncAdminUsersToAuth0(token, request);
            },
            onSuccess: invalidateUserData,
        }),
    };
}

export function useAdminCompanyAdminMutations(companyId: number | null) {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const invalidateCompanyAdminData = async () => {
        if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
            return;
        }
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanyAdmins(companyId) }),
            queryClient.invalidateQueries({ queryKey: [...queryKeys.all, 'admin-users'] }),
            queryClient.invalidateQueries({ queryKey: queryKeys.adminCompanyTeams(companyId) }),
        ]);
    };

    return {
        updateCompanyUserAdmin: useMutation({
            mutationFn: async ({ userId, isAdmin }: { userId: number; isAdmin: boolean }) => {
                if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                    throw new Error('Company ID is required');
                }
                const token = await getAccessTokenSilently();
                const request: UpdateAdminCompanyUserAdminRequest = { isAdmin };
                return apiClient.updateAdminCompanyUserAdmin(token, companyId, userId, request);
            },
            onSuccess: invalidateCompanyAdminData,
        }),
    };
}

export function useAdminCompanyMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return {
        createCompany: useMutation({
            mutationFn: async (request: CreateAdminCompanyRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createAdminCompany(token, request);
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: [...queryKeys.all, 'admin-companies'],
                });
            },
        }),
        updateCompany: useMutation({
            mutationFn: async ({
                companyId,
                request,
            }: {
                companyId: number;
                request: UpdateAdminCompanyRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updateAdminCompany(token, companyId, request);
            },
            onSuccess: async (_result, variables) => {
                await Promise.all([
                    ...getInvalidateAdminCompanyQueryKeys(variables.companyId).map((queryKey) =>
                        queryClient.invalidateQueries({ queryKey }),
                    ),
                    queryClient.invalidateQueries({
                        queryKey: [...queryKeys.all, 'organizations'],
                    }),
                ]);
            },
        }),
        syncCompanyToAuth0: useMutation({
            mutationFn: async (
                request:
                    | number
                    | { companyId: number; syncUsers?: boolean; unsyncedOnly?: boolean },
            ) => {
                const token = await getAccessTokenSilently();
                const { companyId, body } = normalizeCompanySyncRequest(request);
                return apiClient.syncAdminCompanyToAuth0(token, companyId, body);
            },
            onSuccess: async (_result, request) => {
                const { companyId } = normalizeCompanySyncRequest(request);
                await Promise.all(
                    getInvalidateAdminCompanySyncQueryKeys(companyId).map((queryKey) =>
                        queryClient.invalidateQueries({ queryKey }),
                    ),
                );
            },
        }),
        syncCompanyToAuth0SSE: async (
            request: number | { companyId: number; syncUsers?: boolean; unsyncedOnly?: boolean },
            onMessage: (data: AdminCompanySyncSSEEvent) => void,
            onError?: (error: Error) => void,
            onComplete?: (result?: AdminCompanySyncResponse) => void,
        ) => {
            const token = await getAccessTokenSilently();
            const { companyId, body } = normalizeCompanySyncRequest(request);
            return apiClient.syncAdminCompanyToAuth0SSE(
                token,
                companyId,
                body,
                onMessage,
                onError,
                onComplete,
            );
        },
    };
}

export function useAdminIdentityAttention(params: AdminListParams = {}, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminIdentityAttention(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminIdentityAttention(token, params);
        },
        enabled,
        staleTime: 60 * 1000,
    });
}

export function useAdminAuth0BulkSyncCandidates(unsyncedOnly = true, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminAuth0BulkSyncCandidates(unsyncedOnly),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminAuth0BulkSyncCandidates(token, unsyncedOnly);
        },
        enabled,
        staleTime: 30 * 1000,
    });
}

export function useAdminAuth0BulkSyncMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const invalidateIdentityData = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: [...queryKeys.all, 'admin-identity-attention'],
            }),
            queryClient.invalidateQueries({
                queryKey: [...queryKeys.all, 'admin-auth0-bulk-sync-candidates'],
            }),
            queryClient.invalidateQueries({
                queryKey: [...queryKeys.all, 'admin-users'],
            }),
            queryClient.invalidateQueries({
                queryKey: [...queryKeys.all, 'admin-companies'],
            }),
        ]);
    };

    return {
        startJob: useMutation({
            mutationFn: async (request: AdminAuth0BulkSyncStartRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.startAdminAuth0BulkSyncJob(token, request);
            },
        }),
        startJobStream: async (
            request: AdminAuth0BulkSyncStartRequest,
            onMessage: (data: AdminAuth0BulkSyncEvent) => void,
            onError?: (error: Error) => void,
            onComplete?: () => void,
        ) => {
            const token = await getAccessTokenSilently();
            return apiClient.startAdminAuth0BulkSyncJobSSE(
                token,
                request,
                onMessage,
                onError,
                async () => {
                    await invalidateIdentityData();
                    onComplete?.();
                },
            );
        },
        stopJob: useMutation({
            mutationFn: async (jobId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.stopAdminAuth0BulkSyncJob(token, jobId);
            },
        }),
        streamJob: async (
            jobId: string,
            onMessage: (data: AdminAuth0BulkSyncEvent) => void,
            onError?: (error: Error) => void,
            onComplete?: () => void,
        ) => {
            const token = await getAccessTokenSilently();
            return apiClient.streamAdminAuth0BulkSyncJob(
                token,
                jobId,
                onMessage,
                onError,
                async () => {
                    await invalidateIdentityData();
                    onComplete?.();
                },
            );
        },
        invalidateIdentityData,
    };
}

export function useAdminIdentityAttentionMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    return {
        syncAuth0UserToDb: useMutation({
            mutationFn: async (subjectId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.syncAdminAuth0UserToDb(token, subjectId);
            },
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: [...queryKeys.all, 'admin-identity-attention'],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [...queryKeys.all, 'admin-users'],
                    }),
                ]);
            },
        }),
        approveAuth0IdentityGroupLink: useMutation({
            mutationFn: async (email: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.approveAdminAuth0IdentityGroupLink(token, email);
            },
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: [...queryKeys.all, 'admin-identity-attention'],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [...queryKeys.all, 'admin-users'],
                    }),
                ]);
            },
        }),
    };
}

export function useAdminCreditSows(params: AdminCreditSowParams, enabled = true) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminCreditSows(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCreditSows(token, params);
        },
        enabled,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCreditPricingConfigs(
    params: AdminCreditPricingConfigParams,
    enabled = true,
) {
    const { getAccessTokenSilently } = useAuth0();

    return useQuery({
        queryKey: queryKeys.adminCreditPricingConfigs(params),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCreditPricingConfigs(token, params);
        },
        enabled,
        staleTime: 10 * 60 * 1000,
    });
}

export function useAdminPricingConfigActiveSows(
    target: { pricingConfigIds?: readonly string[]; pricingConfigGroupId?: string } | null,
    enabled = true,
) {
    const { getAccessTokenSilently } = useAuth0();
    const sortedIds = [...(target?.pricingConfigIds ?? [])].sort();
    const normalizedTarget = target?.pricingConfigGroupId
        ? { pricingConfigGroupId: target.pricingConfigGroupId }
        : { pricingConfigIds: sortedIds };

    return useQuery({
        queryKey: queryKeys.adminPricingConfigActiveSows(normalizedTarget),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            if (target?.pricingConfigGroupId) {
                return apiClient.getAdminPricingConfigGroupActiveSows(
                    token,
                    target.pricingConfigGroupId,
                );
            }
            if (sortedIds.length === 0) {
                return { sows: [] };
            }
            const responses = await Promise.all(
                sortedIds.map((pricingConfigId) =>
                    apiClient.getAdminPricingConfigActiveSows(token, pricingConfigId),
                ),
            );
            const byId = new Map<string, (typeof responses)[number]['sows'][number]>();
            for (const response of responses) {
                for (const sow of response.sows) {
                    byId.set(sow.id, sow);
                }
            }
            return {
                sows: Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name)),
            };
        },
        enabled: enabled && (sortedIds.length > 0 || Boolean(target?.pricingConfigGroupId)),
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCreditPulseSubscriptionTiers(options?: {
    forSowCreation?: boolean;
    enabled?: boolean;
}) {
    const { getAccessTokenSilently } = useAuth0();
    const forSowCreation = options?.forSowCreation === true;

    return useQuery({
        queryKey: queryKeys.adminCreditPulseSubscriptionTiers(forSowCreation),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCreditPulseSubscriptionTiers(token, { forSowCreation });
        },
        enabled: options?.enabled ?? true,
        staleTime: 10 * 60 * 1000,
    });
}

export function useAdminCompanyPulseSubscriptions(companyId: number | null) {
    const { getAccessTokenSilently } = useAuth0();
    const validCompanyId =
        typeof companyId === 'number' && Number.isSafeInteger(companyId) && companyId > 0;

    return useQuery({
        queryKey: queryKeys.adminCompanyPulseSubscriptions(companyId ?? 0),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminCompanyPulseSubscriptions(token, companyId ?? 0);
        },
        enabled: validCompanyId,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCompanyPulseSubscriptionMutations(companyId: number) {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();
    const invalidateSubscriptions = async () => {
        await queryClient.invalidateQueries({
            queryKey: queryKeys.adminCompanyPulseSubscriptions(companyId),
        });
    };

    const createSubscription = useMutation({
        mutationFn: async (request: AdminPulseSubscriptionInput) => {
            const token = await getAccessTokenSilently();
            return apiClient.createAdminCompanyPulseSubscription(token, companyId, request);
        },
        onSuccess: invalidateSubscriptions,
    });

    const updateSubscription = useMutation({
        mutationFn: async ({
            subscriptionId,
            request,
        }: {
            subscriptionId: string;
            request: AdminPulseSubscriptionInput;
        }) => {
            const token = await getAccessTokenSilently();
            return apiClient.updateAdminCompanyPulseSubscription(
                token,
                companyId,
                subscriptionId,
                request,
            );
        },
        onSuccess: invalidateSubscriptions,
    });

    return { createSubscription, updateSubscription };
}

export function useAdminTeamMembers(teamIds: readonly number[], enabled = true) {
    const { getAccessTokenSilently } = useAuth0();
    const sortedKey = [...teamIds]
        .slice()
        .sort((a, b) => a - b)
        .join(',');

    return useQuery({
        queryKey: queryKeys.adminTeamMembers(sortedKey),
        queryFn: async () => {
            const token = await getAccessTokenSilently();
            return apiClient.getAdminTeamMembers(token, [...teamIds]);
        },
        enabled: enabled && teamIds.length > 0,
        staleTime: 2 * 60 * 1000,
    });
}

export function useCreditMutations() {
    const { getAccessTokenSilently } = useAuth0();
    const queryClient = useQueryClient();

    const invalidateCreditData = async () => {
        await queryClient.invalidateQueries({ queryKey: [...queryKeys.all] });
    };

    return {
        createSow: useMutation({
            mutationFn: async (request: CreateSowRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createSow(token, request);
            },
            onSuccess: invalidateCreditData,
        }),
        updateSow: useMutation({
            mutationFn: async ({
                sowId,
                request,
            }: {
                sowId: string;
                request: UpdateSowRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updateSow(token, sowId, request);
            },
            onSuccess: async (data, variables) => {
                queryClient.setQueryData(queryKeys.sow(variables.sowId), data);
                await invalidateCreditData();
            },
        }),
        archiveSow: useMutation({
            mutationFn: async (sowId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.archiveSow(token, sowId);
            },
            onSuccess: async (data, sowId) => {
                queryClient.setQueryData(queryKeys.sow(sowId), data);
                await invalidateCreditData();
            },
        }),
        updateSowWriteAccess: useMutation({
            mutationFn: async ({
                sowId,
                request,
            }: {
                sowId: string;
                request: UpdateSowWriteAccessRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updateSowWriteAccess(token, sowId, request);
            },
            onSuccess: async (data, variables) => {
                queryClient.setQueryData(queryKeys.sowWriteAccess(variables.sowId), data);
                await queryClient.invalidateQueries({
                    queryKey: queryKeys.sow(variables.sowId),
                });
                await invalidateCreditData();
            },
        }),
        createCreditEvent: useMutation({
            mutationFn: async (request: CreateCreditEventRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createCreditEvent(token, request);
            },
            onSuccess: invalidateCreditData,
        }),
        createCreditImport: useMutation({
            mutationFn: async (request: CreateCreditImportRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createCreditImport(token, request);
            },
        }),
        validateCreditImport: useMutation({
            mutationFn: async (batchId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.validateCreditImport(token, batchId);
            },
        }),
        applyCreditImport: useMutation({
            mutationFn: async (batchId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.applyCreditImport(token, batchId);
            },
            onSuccess: invalidateCreditData,
        }),
        createCreditReservation: useMutation({
            mutationFn: async (request: CreateCreditReservationRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createCreditReservation(token, request);
            },
            onSuccess: invalidateCreditData,
        }),
        commitCreditReservation: useMutation({
            mutationFn: async ({
                reservationId,
                request,
            }: {
                reservationId: string;
                request: CreditReservationMutationRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.commitCreditReservation(token, reservationId, request);
            },
            onSuccess: invalidateCreditData,
        }),
        adjustCreditReservation: useMutation({
            mutationFn: async ({
                reservationId,
                request,
            }: {
                reservationId: string;
                request: CreditReservationAdjustmentRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.adjustCreditReservation(token, reservationId, request);
            },
            onSuccess: invalidateCreditData,
        }),
        cancelCreditReservation: useMutation({
            mutationFn: async ({
                reservationId,
                request,
            }: {
                reservationId: string;
                request: CreditReservationMutationRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.cancelCreditReservation(token, reservationId, request);
            },
            onSuccess: invalidateCreditData,
        }),
        reassignLedgerEntry: useMutation({
            mutationFn: async ({
                entryId,
                request,
            }: {
                entryId: string;
                request: ReassignLedgerEntryRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.reassignLedgerEntry(token, entryId, request);
            },
            onSuccess: invalidateCreditData,
        }),
        previewCompanyHistoricalSowImport: useMutation({
            mutationFn: async (companyId: number) => {
                const token = await getAccessTokenSilently();
                return apiClient.previewCompanyHistoricalSowImport(token, companyId);
            },
        }),
        previewCreateSowHistoricalImport: useMutation({
            mutationFn: async (request: CreateSowHistoricalImportPreviewRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.previewCreateSowHistoricalImport(token, request);
            },
        }),
        applyCompanyHistoricalSowImport: useMutation({
            mutationFn: async ({
                companyId,
                request,
            }: {
                companyId: number;
                request: ApplyCompanyHistoricalLedgerImportRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.applyCompanyHistoricalSowImport(token, companyId, request);
            },
            onSuccess: invalidateCreditData,
        }),
        createPricingConfig: useMutation({
            mutationFn: async (request: CreatePricingConfigRequest) => {
                const token = await getAccessTokenSilently();
                return apiClient.createPricingConfig(token, request);
            },
            onSuccess: invalidateCreditData,
        }),
        updatePricingConfig: useMutation({
            mutationFn: async ({
                id,
                request,
            }: {
                id: string;
                request: UpdatePricingConfigRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.updatePricingConfig(token, id, request);
            },
            onSuccess: invalidateCreditData,
        }),
        createPricingConfigVersion: useMutation({
            mutationFn: async ({
                id,
                request,
            }: {
                id: string;
                request: CreatePricingConfigVersionRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.createPricingConfigVersion(token, id, request);
            },
            onSuccess: invalidateCreditData,
        }),
        deactivatePricingConfig: useMutation({
            mutationFn: async (pricingConfigId: string) => {
                const token = await getAccessTokenSilently();
                return apiClient.deactivatePricingConfig(token, pricingConfigId);
            },
            onSuccess: invalidateCreditData,
        }),
        syncPulseSeatAssignments: useMutation({
            mutationFn: async ({
                subscriptionId,
                userIds,
            }: {
                subscriptionId: string;
                userIds: number[];
                sowId: string;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.syncPulseSeatAssignments(token, { subscriptionId, userIds });
            },
            onSuccess: async (_data, variables) => {
                await queryClient.invalidateQueries({ queryKey: queryKeys.sow(variables.sowId) });
                await queryClient.invalidateQueries({
                    queryKey: [...queryKeys.all, 'admin-team-members'],
                });
            },
        }),
        changeSowPulseSeatQuantity: useMutation({
            mutationFn: async ({
                sowId,
                request,
            }: {
                sowId: string;
                request: ChangeSowPulseSeatQuantityRequest;
            }) => {
                const token = await getAccessTokenSilently();
                return apiClient.changeSowPulseSeatQuantity(token, sowId, request);
            },
            onSuccess: async (_data, variables) => {
                await queryClient.invalidateQueries({ queryKey: queryKeys.sow(variables.sowId) });
            },
        }),
    };
}

// Type for organization teams API response
interface OrganizationTeamsResponse {
    success: boolean;
    organization: {
        id: string;
        name: string;
        displayName?: string;
        companyId: string;
        /** Set by admin GET /admin/companies/:id/teams from wevo.company.is_active */
        isActive?: boolean;
    };
    teams: Array<{
        id: string;
        name: string;
        /** Present on admin company teams; org-scoped list omits (all active). */
        isActive?: boolean;
        /** Org teams endpoint: count of active members in payload. */
        memberCount?: number;
        /** Admin company teams: active team_to_user rows. */
        activeMemberCount?: number;
        members: Array<{
            user: {
                id: string | number;
                email: string;
                name: string;
            };
            /** Admin company teams: team_to_user.is_active for this row. */
            membershipActive?: boolean;
        }>;
    }>;
    unassignedUsers?: Array<{
        id: string;
        email: string;
        name: string;
        picture?: string;
        roles: string[];
        publicId?: string;
    }>;
}

// Hook to get organization teams
export function useOrganizationTeams(orgId: string | null) {
    const { apiCall } = useApi();

    return useQuery<OrganizationTeamsResponse>({
        queryKey: queryKeys.organizationTeams(orgId || ''),
        queryFn: () => apiCall(`/organizations/${orgId}/teams`),
        enabled: !!orgId, // Only fetch when orgId is provided
        staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    });
}

/** Teams for a DB company (wevo.team) — use on admin company detail where org resolution may fail. */
export function useAdminCompanyTeams(companyId: number | null) {
    const { apiCall } = useApi();

    return useQuery<OrganizationTeamsResponse>({
        queryKey: queryKeys.adminCompanyTeams(companyId ?? 0),
        queryFn: () => apiCall(`/admin/companies/${companyId}/teams`),
        enabled: typeof companyId === 'number' && Number.isFinite(companyId) && companyId > 0,
        staleTime: 2 * 60 * 1000,
    });
}

export function useAdminCompanyTeamActiveMutation(companyId: number | null) {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            teamId,
            isActive,
            name,
        }: {
            teamId: number;
            isActive?: boolean;
            name?: string;
        }) => {
            if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                throw new Error('Company ID is required');
            }
            return apiCall<{ success: boolean; id: number; name: string; isActive: boolean }>(
                `/admin/companies/${companyId}/teams/${teamId}`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({ ...(name !== undefined ? { name } : {}), isActive }),
                },
            );
        },
        onSuccess: () => {
            if (companyId != null && Number.isFinite(companyId) && companyId > 0) {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.adminCompanyTeams(companyId),
                });
            }
        },
    });
}

export function useAdminCompanyTeamMembershipMutation(companyId: number | null) {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            teamId,
            userId,
            isActive,
        }: {
            teamId: number;
            userId: number;
            isActive: boolean;
        }) => {
            if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                throw new Error('Company ID is required');
            }
            return apiCall<{
                success: boolean;
                teamId: number;
                userId: number;
                membershipActive: boolean;
            }>(`/admin/companies/${companyId}/teams/${teamId}/members/${userId}`, {
                method: 'PATCH',
                body: JSON.stringify({ isActive }),
            });
        },
        onSuccess: async () => {
            if (companyId != null && Number.isFinite(companyId) && companyId > 0) {
                await queryClient.refetchQueries({
                    queryKey: queryKeys.adminCompanyTeams(companyId),
                });
                await queryClient.invalidateQueries({
                    queryKey: [...queryKeys.all, 'admin-users'],
                });
            }
        },
    });
}

export function useAdminCreateCompanyTeamMutation(companyId: number | null) {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { name: string; isActive: boolean }) => {
            if (companyId == null || !Number.isFinite(companyId) || companyId <= 0) {
                throw new Error('Company ID is required');
            }
            return apiCall<{
                success: boolean;
                team: { id: number; name: string; isActive: boolean };
            }>(`/admin/companies/${companyId}/teams`, {
                method: 'POST',
                body: JSON.stringify(input),
            });
        },
        onSuccess: () => {
            if (companyId != null && Number.isFinite(companyId) && companyId > 0) {
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.adminCompanyTeams(companyId),
                });
            }
        },
    });
}

// Hook to get organization groups
// Removed unused hook: useOrganizationGroups (no corresponding backend route)

// Create group mutation
// Removed unused hook: useCreateGroup

// Update group mutation
// Removed unused hook: useUpdateGroup

// Delete group mutation
// Removed unused hook: useDeleteGroup

// Add user to group mutation
// Removed unused hook: useAddUserToGroup

// Remove user from group mutation
// Removed unused hook: useRemoveUserFromGroup

// Update user group leader status mutation
// Removed unused hook: useUpdateUserGroupLeaderStatus

// Move user between groups mutation
// Removed unused hook: useMoveUserBetweenGroups

// Removed unused hook: useLegacyUser (unsupported endpoint)

export function useSyncUserData() {
    const { getAccessTokenSilently } = useAuth0();
    return useMutation({
        mutationFn: async (userId: string) => {
            const token = await getAccessTokenSilently();
            return apiClient.syncUserData(token, userId);
        },
    });
}

// === NEW SIMPLIFIED GROUPS AND USERS HOOKS ===

// Update user team role (organization admin version)
export function useUpdateUserTeamRoleOrgAdmin() {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            orgId,
            teamId,
            userId,
            team_role_id,
        }: {
            orgId: string;
            teamId: string;
            userId: string;
            team_role_id: number;
        }) => {
            return apiCall(`/organizations/${orgId}/teams/${teamId}/members/${userId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    team_role_id: team_role_id, // 1 = Pro Restricted, 2 = Pro User, 3 = Pro Approver
                }),
            });
        },
        onSuccess: async (_, variables) => {
            await Promise.all([
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationTeams(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationUsers(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: [...queryKeys.all, 'database-users'],
                }),
            ]);
        },
        onError: (error) => {
            console.error('Failed to update user team role (org admin):', error);
        },
    });
}

// Add user to team (organization admin version)
export function useAddUserToTeamOrgAdmin() {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            orgId,
            teamId,
            user_id,
            team_role_id = 2,
        }: {
            orgId: string;
            teamId: string;
            user_id: string;
            team_role_id?: number;
        }) => {
            return apiCall(`/organizations/${orgId}/teams/${teamId}/members`, {
                method: 'POST',
                body: JSON.stringify({
                    user_id,
                    team_role_id,
                }),
            });
        },
        onSuccess: async (_, variables) => {
            await Promise.all([
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationTeams(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationUsers(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: [...queryKeys.all, 'database-users'],
                }),
            ]);
        },
        onError: (error) => {
            console.error('Failed to add user to team (org admin):', error);
        },
    });
}

// Remove user from team (organization admin version)
export function useRemoveUserFromTeamOrgAdmin() {
    const { apiCall } = useApi();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            orgId,
            teamId,
            userId,
        }: {
            orgId: string;
            teamId: string;
            userId: string;
        }) => {
            return apiCall(`/organizations/${orgId}/teams/${teamId}/members/${userId}`, {
                method: 'DELETE',
            });
        },
        onSuccess: async (_, variables) => {
            await Promise.all([
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationTeams(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: queryKeys.organizationUsers(variables.orgId),
                }),
                queryClient.refetchQueries({
                    queryKey: [...queryKeys.all, 'database-users'],
                }),
            ]);
        },
        onError: (error) => {
            console.error('Failed to remove user from team (org admin):', error);
        },
    });
}
